import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { initGA, trackPageView, trackPhoneCallConversion, trackDirectionsClick } from './services/analytics';
import { ThemeProvider } from './contexts/ThemeContext';

// Critical path - load immediately
import HomeNew from './pages/HomeNew';
import Header from './components/Header';
import TrustBar from './components/TrustBar';
const Footer = lazy(() => import('./components/Footer'));
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import { StickyBannerNotice } from './components/WebsiteNotices';
import { Toaster } from './components/ui/sonner';
import CanonicalURL from './shared/CanonicalURL';
import NoIndexRobots from './shared/NoIndexRobots';
import BreadcrumbSchema from './shared/BreadcrumbSchema';
import ErrorBoundary from './components/ErrorBoundary';
import GeoGate from './components/GeoGate';
import appleBuybackContent from './data/appleBuybackPages.json';

// Lazy load non-critical components for better performance
const PhoneCategoriesPage = lazy(() => import('./pages/PhoneCategoriesPage'));
const PhoneModelsPage = lazy(() => import('./pages/iPhoneModelsPage'));
const PhoneRepairPage = lazy(() => import('./pages/iPhoneRepairPage'));
const RepairCategoriesPage = lazy(() => import('./pages/RepairCategoriesPage'));
const RepairFlowPage = lazy(() => import('./pages/RepairFlowPage'));
const PhoneRepairBrandsPage = lazy(() => import('./pages/PhoneRepairBrandsPage'));
const PhoneBrandRepairPage = lazy(() => import('./pages/PhoneBrandRepairPage'));
const SellDevicePage = lazy(() => import('./pages/SellDevicePage'));
const SellCategoryPage = lazy(() => import('./pages/SellCategoryPage'));
const SellBrandPage = lazy(() => import('./pages/SellBrandPage'));
const SellValuationPage = lazy(() => import('./pages/SellValuationPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const GetDirectionsPage = lazy(() => import('./pages/GetDirectionsPage'));
const StateCitiesPage = lazy(() => import('./pages/StateCitiesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const OurTeamPage = lazy(() => import('./pages/OurTeamPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const StoreAdminPanel = lazy(() => import('./pages/StoreAdminPanel'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminResetPassword = lazy(() => import('./pages/AdminResetPassword'));
const CustomerResetPassword = lazy(() => import('./pages/CustomerResetPassword'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const MailInPage = lazy(() => import('./pages/MailInPage'));
const MobileRepairSalePage = lazy(() => import('./pages/MobileRepairSalePage'));
const CustomerRegister = lazy(() => import('./pages/CustomerRegister'));
const CustomerLogin = lazy(() => import('./pages/CustomerLogin'));
const CustomerProfile = lazy(() => import('./pages/CustomerProfile'));
const RepairServicePage = lazy(() => import('./pages/RepairServicePage'));
const SellServicePage = lazy(() => import('./pages/SellServicePage'));
const SellOtherPage = lazy(() => import('./pages/SellOtherPage'));
// SEO consolidation (Jun 2026): thin city/neighborhood + service×neighborhood landing
// page components retired, their routes now 301-redirect to the localized master hubs.
const SellDeviceSEOPage = lazy(() => import('./pages/SellDeviceSEOPage'));
const AppleBuybackPage = lazy(() => import('./pages/AppleBuybackPage'));
const SellComparisonPage = lazy(() => import('./pages/SellComparisonPage'));
const DeviceValueGuidePage = lazy(() => import('./pages/DeviceValueGuidePage'));
const SeasonalSellPage = lazy(() => import('./pages/SeasonalSellPage'));
const SustainabilityPage = lazy(() => import('./pages/SustainabilityPage'));
const SellReviewsPage = lazy(() => import('./pages/SellReviewsPage'));
const RepairQuotePage = lazy(() => import('./pages/RepairQuotePage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const ScreenReplacementPage = lazy(() => import('./pages/ScreenReplacementPage'));
const SellBrandHubPage = lazy(() => import('./pages/SellBrandHubPage'));
const BuybackPage = lazy(() => import('./pages/BuybackPage'));
const SellStoragePage = lazy(() => import('./pages/SellStoragePage'));
const SellCarrierPage = lazy(() => import('./pages/SellCarrierPage'));
const SellOrKeepCalculator = lazy(() => import('./pages/SellOrKeepCalculator'));
const SellStatePage = lazy(() => import('./pages/SellStatePage'));
const SellYearPage = lazy(() => import('./pages/SellYearPage'));
const BundleSellPage = lazy(() => import('./pages/BundleSellPage'));
const AllRepairsPage = lazy(() => import('./pages/AllRepairsPage'));
const AllSellPage = lazy(() => import('./pages/AllSellPage'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const WarrantyPage = lazy(() => import('./pages/WarrantyPage'));
const WarrantyLookupPage = lazy(() => import('./pages/WarrantyLookupPage'));
const RepairProtectionPlanPage = lazy(() => import('./pages/RepairProtectionPlanPage'));
const SmsTermsPage = lazy(() => import('./pages/SmsTermsPage'));
const SmsSignupPage = lazy(() => import('./pages/SmsSignupPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const FAQFullPage = lazy(() => import('./pages/FAQFullPage'));
// 2A Forum (community Q&A)
const ForumHomePage = lazy(() => import('./pages/ForumPages').then(m => ({ default: m.ForumHomePage })));
const ForumCategoryPage = lazy(() => import('./pages/ForumPages').then(m => ({ default: m.ForumCategoryPage })));
const ForumThreadPage = lazy(() => import('./pages/ForumPages').then(m => ({ default: m.ForumThreadPage })));
const ForumNewThreadPage = lazy(() => import('./pages/ForumPages').then(m => ({ default: m.ForumNewThreadPage })));
const ForumSearchPage = lazy(() => import('./pages/ForumPages').then(m => ({ default: m.ForumSearchPage })));
// Programmatic SEO Keyword Pages
const KeywordsHubPage = lazy(() => import('./pages/KeywordPages').then(m => ({ default: m.KeywordsHubPage })));
const KeywordDetailPage = lazy(() => import('./pages/KeywordPages').then(m => ({ default: m.KeywordDetailPage })));
// Redirect /keywords/:slug → /guides/:slug for backward compatibility
const KeywordsRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/guides/${slug}`} replace />;
};
const TickerBanner = lazy(() => import('./components/TickerBanner'));
const StoreManagement = lazy(() => import('./components/StoreManagement'));
const CounterOfferResponse = lazy(() => import('./pages/CounterOfferResponse'));

// Legal Pages - lazy loaded
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const UserAgreement = lazy(() => import('./pages/legal/UserAgreement'));
const LawEnforcement = lazy(() => import('./pages/legal/LawEnforcement'));
const AccessibilityStatement = lazy(() => import('./pages/legal/AccessibilityStatement'));
const AccessibilityFeedback = lazy(() => import('./pages/legal/AccessibilityFeedback'));
const DSAR = lazy(() => import('./pages/legal/DSAR'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));

// Service Areas - State & City Pages for SEO
const ServiceAreasPage = lazy(() => import('./pages/ServiceAreasPage'));
const ChicagoServiceAreasPage = lazy(() => import('./pages/ChicagoServiceAreasPage'));
const StatePage = lazy(() => import('./pages/StatePage'));
const CityPage = lazy(() => import('./pages/CityPage'));

// SEO Pages - Near Me
const NearMeRepairPage = lazy(() => import('./pages/NearMeRepairPage'));
const NearMeSellPage = lazy(() => import('./pages/NearMeSellPage'));

// SEO Pages - Local Services Hub + Individual Pages
const LocalServicesHub = lazy(() => import('./pages/LocalServicesHub'));
const LocalServicePage = lazy(() => import('./pages/LocalServicePage'));
const SEOCategoryHub = lazy(() => import('./pages/SEOCategoryHub'));

// SEO Pages - Model Specific
const IPhoneRepairSEOPage = lazy(() => import('./pages/IPhoneRepairSEOPage'));
const SamsungRepairSEOPage = lazy(() => import('./pages/SamsungRepairSEOPage'));
const IPadRepairSEOPage = lazy(() => import('./pages/IPadRepairSEOPage'));

// Feature pages - lazy loaded
const RepairStatusTracker = lazy(() => import('./pages/RepairStatusTracker'));
const SellStatusTracker = lazy(() => import('./pages/SellStatusTracker'));
const BookAppointmentPage = lazy(() => import('./pages/BookAppointmentPage'));
const TrackRepairPage = lazy(() => import('./pages/TrackRepairPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const TradeInPage = lazy(() => import('./pages/TradeInPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const DiagnosticPage = lazy(() => import('./pages/DiagnosticPage'));

// E-Commerce Pages - lazy loaded
const ShopPage = lazy(() => import('./pages/ShopPage'));
const RefurbishedPage = lazy(() => import('./pages/RefurbishedPage'));
const RefurbishedProductPage = lazy(() => import('./pages/RefurbishedProductPage'));
const RefurbishedCartPage = lazy(() => import('./pages/RefurbishedCartPage'));
const RefurbishedOrderSuccess = lazy(() => import('./pages/RefurbishedOrderSuccess'));
const BusinessPage = lazy(() => import('./pages/BusinessPage'));

// Local SEO Pages - lazy loaded
const LocalSEOPage = lazy(() => import('./pages/LocalSEOPage'));
const LocalZipPage = lazy(() => import('./pages/LocalZipPage'));
const LocalAreasPage = lazy(() => import('./pages/LocalAreasPage'));

// New SEO Feature Pages - lazy loaded
const GoogleBusinessPage = lazy(() => import('./pages/GoogleBusinessPage'));
const SocialMediaPage = lazy(() => import('./pages/SocialMediaPage'));
const SocialPlatformPage = lazy(() => import('./pages/SocialPlatformPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const ServiceLandingPage = lazy(() => import('./pages/ServiceLandingPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const WhatWeDoPage = lazy(() => import('./pages/WhatWeDoPage'));
const FAQSchemaPage = lazy(() => import('./pages/FAQSchemaPage'));
const SitemapXmlRedirect = lazy(() => import('./pages/SitemapXmlRedirect'));

// Price Comparison Page
const PriceComparisonPage = lazy(() => import('./pages/PriceComparisonPage'));

// AI Answers Hub, AEO (Answer Engine Optimization) for ChatGPT, Perplexity, Claude, Gemini
const AnswersHub = lazy(() => import('./pages/AnswersHub'));
const AnswerDetailPage = lazy(() => import('./pages/AnswerDetailPage'));
const NearbyCityLandingPage = lazy(() => import('./pages/NearbyCityLandingPage'));
const AIRankMonitorPage = lazy(() => import('./pages/AIRankMonitorPage'));
const SeoGrowthAdminPage = lazy(() => import('./pages/SeoGrowthAdminPage'));
const SearchConsoleAdminPage = lazy(() => import('./pages/SearchConsoleAdminPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const PartnershipsPage = lazy(() => import('./pages/PartnershipsPage'));

// SEO Growth, Free Tools Suite (backlink magnets)
const ToolsHubPage = lazy(() => import('./pages/tools/ToolsHubPage'));
const FreeRepairResourcesPage = lazy(() => import('./pages/FreeRepairResourcesPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));const IMEICheckerPage = lazy(() => import('./pages/tools/IMEICheckerPage'));
const RepairCostCalculatorPage = lazy(() => import('./pages/tools/RepairCostCalculatorPage'));
const TradeInEstimatorPage = lazy(() => import('./pages/tools/TradeInEstimatorPage'));
const WaterDamageQuizPage = lazy(() => import('./pages/tools/WaterDamageQuizPage'));

// Programmatic SEO, /fix/:device/:symptom
// SEO consolidation (Jun 2026): /fix/:device/:symptom problem pages now redirect to brand hubs.

// SEO Growth, Schema Plus (HowTo + VideoObject + AggregateRating)
const SchemaPlusInjector = lazy(() => import('./components/seo/SchemaPlusInjector'));

// SEO Growth Pack 2, Mega schema (Org KG, Sitelinks Search Box, DiscussionForum, QAPage, Reviews, etc.)
const MegaSchemaInjector = lazy(() => import('./components/seo/MegaSchemaInjector'));

// SPEED, invisibly prefetch internal links on hover (no UI change)
const HoverPrefetch = lazy(() => import('./components/seo/HoverPrefetch'));

// SEO Growth Pack 2, Topical Authority Pillar Pages
const PillariPhoneHub = lazy(() => import('./pages/seo/PillariPhoneHub'));
const PillarSamsungHub = lazy(() => import('./pages/seo/PillarSamsungHub'));
const PillarMacBookHub = lazy(() => import('./pages/seo/PillarMacBookHub'));

// SEO Growth Pack 2, Authors / Q&A / Comparisons / Gallery / Press
const AuthorsTeamPage = lazy(() => import('./pages/seo/AuthorsTeamPage'));
const QAPage = lazy(() => import('./pages/seo/QAPage'));
const QAIndexPage = lazy(() => import('./pages/seo/QAPage').then(m => ({ default: m.QAIndexPage })));
const ComparisonPage = lazy(() => import('./pages/seo/ComparisonPage'));
const BeforeAfterGallery = lazy(() => import('./pages/seo/BeforeAfterGallery'));
const PressKitPage = lazy(() => import('./pages/seo/PressKitPage'));
const UbreakifixAlternativePage = lazy(() => import('./pages/UbreakifixAlternativePage'));
const ReviewsAiSeoPage = lazy(() => import('./pages/ReviewsAiSeoPage'));
// Hidden internal tool, PIN-gated; NOT linked, NOT in sitemap
const Estimator2APage = lazy(() => import('./pages/Estimator2APage'));

// Performance helpers (CSS containment + content-visibility for new SEO pages)
import './components/seo/seo-perf.css';

// Repair SEO Pages - lazy loaded
const RepairBrandHubPage = lazy(() => import('./pages/RepairBrandHubPage'));
const RepairComparisonPage = lazy(() => import('./pages/RepairComparisonPage'));
const RepairCostGuidePage = lazy(() => import('./pages/RepairCostGuidePage'));
const RepairStatePage = lazy(() => import('./pages/RepairStatePage'));
const RepairReviewsPage = lazy(() => import('./pages/RepairReviewsPage'));
const RepairOrReplaceCalculator = lazy(() => import('./pages/RepairOrReplaceCalculator'));

// Long-form SEO content hub pages
const CompleteRepairGuide = lazy(() => import('./pages/CompleteRepairGuide'));
const CompleteSellGuide = lazy(() => import('./pages/CompleteSellGuide'));

// Apple Independent Repair Provider Page
const AppleRepairProviderPage = lazy(() => import('./pages/AppleRepairProviderPage'));

// Symptom Checker Page
const SymptomCheckerPage = lazy(() => import('./pages/SymptomCheckerPage'));

// Case Studies Pages
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const CaseStudyDetailPage = lazy(() => import('./pages/CaseStudyDetailPage'));

// Repair SEO Content Pages
const HowItWorksRepairPage = lazy(() => import('./pages/seo/repair/HowItWorksRepairPage'));
const RepairChecklistPage = lazy(() => import('./pages/seo/repair/RepairChecklistPage'));
const FAQRepairPage = lazy(() => import('./pages/seo/repair/FAQRepairPage'));
const EnvironmentalImpactRepairPage = lazy(() => import('./pages/seo/repair/EnvironmentalImpactRepairPage'));
const DataSecurityRepairPage = lazy(() => import('./pages/seo/repair/DataSecurityRepairPage'));
const WhyUsRepairPage = lazy(() => import('./pages/seo/repair/WhyUsRepairPage'));
const RepairVsBuyNewPage = lazy(() => import('./pages/seo/repair/RepairVsBuyNewPage'));

// UI Components - lazy loaded
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const AccessibilityWidget = lazy(() => import('./components/AccessibilityWidget'));

// SEO Landing Pages - Programmatic SEO
const SEOLandingPage = lazy(() => import('./pages/SEOLandingPage'));

// 404 Page - lazy loaded
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Web Design Microsite - lazy loaded
const WebDesignLayout = lazy(() => import('./pages/webdesign/WebDesignLayout'));
const WebDesignHome = lazy(() => import('./pages/webdesign/WebDesignHome'));
const WebDesignServices = lazy(() => import('./pages/webdesign/WebDesignServices'));
const WebDesignHowItWorks = lazy(() => import('./pages/webdesign/WebDesignHowItWorks'));
const WebDesignAbout = lazy(() => import('./pages/webdesign/WebDesignAbout'));
const WebDesignContact = lazy(() => import('./pages/webdesign/WebDesignContact'));

// Owner Control Center and Technician Management - lazy loaded
const ResaleIntelligence = lazy(() => import('./pages/ResaleIntelligence'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
  </div>
);

// Protected Route component for admin - COMPLETELY SEPARATE from Customer Portal
const ProtectedAdminRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/admin21/login" replace />;
  }
  return children;
};

// Protected Route component for customer - COMPLETELY SEPARATE from Admin/Employee
const ProtectedCustomerRoute = ({ children }) => {
  const token = localStorage.getItem('customer_token');
  if (!token) {
    return <Navigate to="/customer/login" replace />;
  }
  return children;
};

// Wrapper to conditionally show ticker banner (hide on admin routes)
const ConditionalTickerBanner = () => {
  const [showTicker, setShowTicker] = React.useState(true);
  
  React.useEffect(() => {
    const checkPath = () => {
      const path = window.location.pathname;
      setShowTicker(!path.startsWith('/admin21') && !path.startsWith('/employee-portal'));
    };
    checkPath();
    window.addEventListener('popstate', checkPath);
    return () => window.removeEventListener('popstate', checkPath);
  }, []);
  
  if (!showTicker) return null;
  return <TickerBanner />;
};

// Wrapper to conditionally show footer (hide on admin and employee portal)
// Footer is below-the-fold; we defer its mount by one paint frame so it never
// competes with above-the-fold render. No logic change, just timing.
const ConditionalFooter = () => {
  const location = useLocation();
  const path = location.pathname;
  const [mount, setMount] = React.useState(false);

  React.useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    const id = idle(() => setMount(true), { timeout: 800 });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  // Hide footer on admin panel, employee portal, store admin, landing pages, webdesign microsite, and LSA /what-we-do (has its own minimal footer)
  if (path.startsWith('/admin') || path.startsWith('/employee-portal') || path.startsWith('/store') || path === '/screen-replacement' || path.startsWith('/webdesign') || path === '/what-we-do') {
    return null;
  }

  if (!mount) return null;

  return (
    <Suspense fallback={null}>
      <TrustBar />
      <Footer />
    </Suspense>
  );
};

// ── SEO consolidation (Jun 2026) ──
// Thin programmatic neighborhood + device-issue pages are merged into a small set of
// comprehensive master hubs. Every legacy URL 301-redirects (client-side Navigate
// replace, matching the site's existing redirect pattern) to preserve crawl equity.
// Neighborhood/city/"near me" pages → localized master hubs (/phone-repair-chicago,
// /sell-phone-chicago). Device-problem pages → brand hubs (/iphone-hub, /samsung-hub).
function RepairNearRedirect() {
  return <Navigate to="/phone-repair-chicago" replace />;
}

// /fix/:device/:symptom problem pages → matching brand hub (fallback: repair master).
function FixSymptomRedirect() {
  const { device } = useParams();
  const d = (device || '').toLowerCase();
  if (d.includes('iphone') || d.includes('apple')) return <Navigate to="/iphone-hub" replace />;
  if (d.includes('samsung') || d.includes('galaxy')) return <Navigate to="/samsung-hub" replace />;
  if (d.includes('macbook')) return <Navigate to="/macbook-hub" replace />;
  return <Navigate to="/phone-repair-chicago" replace />;
}

// Domain-migration safety net (Jun 2026): map every unmatched legacy URL from the old
// 4,000-page programmatic setup to its CLOSEST active page so historical link equity is
// preserved instead of hitting a 404. Brand + intent aware. Only genuinely irrelevant
// URLs fall through to a real 404 (correct SEO).
function SmartLegacyNotFound() {
  const { pathname } = useLocation();
  const p = pathname.toLowerCase();

  // Sell / buyback / trade intent (checked so "sell-iphone-x" → sell page, not repair hub)
  const sell = /(sell|buyback|buy-back|cash-for|cash4|trade-in|tradein|we-buy|pawn|value|worth|appraisal)/.test(p);

  // Brand-specific legacy URLs → matching hub (sell vs repair)
  if (/(samsung|galaxy)/.test(p)) return <Navigate to={sell ? '/sell-samsung' : '/samsung-hub'} replace />;
  if (/(iphone|apple)/.test(p)) return <Navigate to={sell ? '/sell-iphone' : '/iphone-hub'} replace />;
  if (/(macbook|imac|mac-mini|mac-)/.test(p)) return <Navigate to={sell ? '/sell-macbook' : '/macbook-hub'} replace />;
  if (/(ipad)/.test(p)) return <Navigate to={sell ? '/sell-ipad' : '/ipad-repair-chicago'} replace />;
  if (/(ps5|ps4|playstation|xbox|nintendo|switch|console|gaming)/.test(p)) return <Navigate to="/game-console-repair-chicago" replace />;
  if (/(laptop|computer|desktop|pc-repair)/.test(p)) return <Navigate to={sell ? '/sell-macbook' : '/laptop-repair-chicago'} replace />;
  if (/(pixel|google-phone|oneplus|motorola|lg-)/.test(p)) return <Navigate to={sell ? '/sell-phone-chicago' : '/phone-repair-chicago'} replace />;

  // Intent-only (no brand keyword)
  if (sell) return <Navigate to="/sell-phone-chicago" replace />;
  if (/(repair|fix|screen|battery|charging|water-damage|cracked|broken|glass|port|camera|speaker|motherboard|logic-board)/.test(p)) return <Navigate to="/phone-repair-chicago" replace />;

  // Content hubs (old guide / blog / answer programmatic pages)
  if (/(guide|blog|answer|article|how-to|tips|cost)/.test(p)) return <Navigate to="/guides" replace />;

  // Local / neighborhood / city / "near me" combos → regional repair hub
  if (/(near-me|near|neighborhood|chicago|illinois|-il|city|local|area)/.test(p)) return <Navigate to="/phone-repair-chicago" replace />;

  // Truly unmatched → genuine 404 (correct for SEO)
  return <NotFoundPage />;
}

function App() {
  return (
    <ThemeProvider>
      <HelmetProvider>
        <ErrorBoundary>
          <div className="App">
            <BrowserRouter>
              <GeoGate>
                <AppContent />
              </GeoGate>
            </BrowserRouter>
          </div>
        </ErrorBoundary>
      </HelmetProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isAppleBuybackPage = appleBuybackContent.pages.some(p => p.path === location.pathname.replace(/\/+$/, ''));
  const isAdminRoute = location.pathname.startsWith('/admin21') || location.pathname.startsWith('/employee-portal') || location.pathname.startsWith('/store-admin');
  const isLandingPage = location.pathname === '/screen-replacement';
  const isWebDesignRoute = location.pathname.startsWith('/webdesign');
  const isLsaPage = location.pathname === '/what-we-do';
  
  // Use centralized API configuration for production compatibility
  const API_URL = (() => {
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    if (!envUrl || envUrl === '') return window.location.origin;
    try {
      const envHost = new URL(envUrl).hostname;
      const currentHost = window.location.hostname;
      if (currentHost === 'localhost' || currentHost === '127.0.0.1' || 
          envHost === currentHost || currentHost.includes('preview.emergentagent.com')) {
        return envUrl;
      }
      return window.location.origin;
    } catch (e) { return window.location.origin; }
  })();
  
  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA();
    
    // Global click handler for phone-call + get-directions conversion tracking
    const handleClick = (e) => {
      const telLink = e.target.closest('a[href^="tel:"]');
      if (telLink) {
        trackPhoneCallConversion(window.location.pathname);
        return;
      }
      const dirLink = e.target.closest(
        'a[href*="google.com/maps"], a[href*="maps.app.goo"], a[href*="goo.gl/maps"], a[href*="maps.apple.com"], a[href*="waze.com"], a[href*="/get-directions"]'
      );
      if (dirLink) {
        trackDirectionsClick(window.location.pathname);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname, isAdminRoute, API_URL]);

  // Route-driven theme: ONLY the homepage stays dark. All other public pages flip to a clean
  // white "light" mode (per product spec: "the screenshot hero stays as is, every other page
  // gets a white background"). Admin/Store/Employee/Web-design routes are excluded, they
  // manage their own theming via [data-admin-panel] CSS overrides.
  useEffect(() => {
    const root = document.documentElement;
    const path = location.pathname;
    const skipThemeSync =
      path.startsWith('/admin') ||
      path.startsWith('/store') ||
      path.startsWith('/employee-portal') ||
      path.startsWith('/webdesign');
    if (skipThemeSync) return;
    const isHome = path === '/';
    if (isHome) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, [location.pathname]);

  // Warm the highest-intent route chunks + Instant Estimate catalog on idle so the
  // Sell / Repair flows open near-instantly on first click (additive; no behavior change).
  useEffect(() => {
    let done = false;
    const warm = () => {
      if (done) return;
      done = true;
      import('./pages/SellDevicePage');   // includes InstantEstimate (static import)
      import('./pages/RepairFlowPage');
      try {
        fetch(`${API_URL}/api/estimate/models?specs_only=true`, { priority: 'low' }).catch(() => {});
      } catch (_e) { /* ignore */ }
    };
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
    const id = ric(warm, { timeout: 3000 });
    return () => {
      const cic = window.cancelIdleCallback || window.clearTimeout;
      try { cic(id); } catch (_e) { /* ignore */ }
    };
  }, [API_URL]);

  return (
    <>
      {/* WCAG 2.1 AA: Skip-to-main-content link, keyboard users tab once to reach this */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-cyan-500 focus:text-slate-950 focus:font-bold focus:shadow-lg focus:outline-none focus:ring-4 focus:ring-cyan-300"
        data-testid="skip-to-main"
      >
        Skip to main content
      </a>
      <ScrollToTop />
      {!isAppleBuybackPage && <SEOHead />}
      {!isAppleBuybackPage && <Suspense fallback={null}><SchemaPlusInjector /></Suspense>}
      {!isAppleBuybackPage && <Suspense fallback={null}><MegaSchemaInjector /></Suspense>}
      <Suspense fallback={null}><HoverPrefetch /></Suspense>
      {!isAppleBuybackPage && <CanonicalURL />}
      <NoIndexRobots />
      {!isLandingPage && !isWebDesignRoute && !isLsaPage && <Header />}
      {!isAdminRoute && !isLandingPage && !isWebDesignRoute && !isLsaPage && <StickyBannerNotice />}
      <main id="main-content" tabIndex="-1">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomeNew />} />
          {/* Redirect /repair to /repairs */}
          <Route path="/repair" element={<Navigate to="/repairs" replace />} />
          {/* New repair flow */}
          <Route path="/repairs" element={<RepairCategoriesPage />} />
          <Route path="/repairs/phones" element={<PhoneRepairBrandsPage />} />
          <Route path="/repairs/phones/:brand" element={<PhoneBrandRepairPage />} />
          <Route path="/repairs/:category" element={<RepairFlowPage />} />
          {/* Legacy repair routes */}
          <Route path="/phone-repairs" element={<PhoneCategoriesPage />} />
          <Route path="/phone-repairs/:category" element={<PhoneModelsPage />} />
          <Route path="/phone/:category/:modelId" element={<PhoneRepairPage />} />
          {/* Sell device */}
          <Route path="/sell" element={<SellDevicePage />} />
          {appleBuybackContent.pages.map(page => <Route key={page.path} path={page.path} element={<AppleBuybackPage />} />)}
          <Route path="/sell/other" element={<SellOtherPage />} />
          <Route path="/sell/:category" element={<SellCategoryPage />} />
          <Route path="/sell/:category/:brandId" element={<SellBrandPage />} />
          <Route path="/sell/:category/:brandId/:modelId" element={<SellValuationPage />} />
          {/* Location */}
          <Route path="/location" element={<LocationPage />} />
          <Route path="/get-directions" element={<GetDirectionsPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/locations/:stateId" element={<StateCitiesPage />} />
          {/* Service Areas - All 50 States SEO Pages */}
          <Route path="/service-areas" element={<ServiceAreasPage />} />
          <Route path="/chicago-service-areas" element={<ChicagoServiceAreasPage />} />
          <Route path="/service-areas/:stateSlug" element={<StatePage />} />
          <Route path="/service-areas/:stateSlug/:citySlug" element={<CityPage />} />
          {/* SEO Near Me Pages */}
          {/* SEO consolidation: redirect near-duplicate "near me" pages to their canonical Chicago hubs (audit Feb 2026) */}
          <Route path="/phone-repair-near-me" element={<SEOLandingPage />} />
          <Route path="/sell-phone-near-me" element={<SEOLandingPage />} />
          <Route path="/sell-laptop-near-me" element={<Navigate to="/sell-laptop-chicago" replace />} />
          <Route path="/iphone-repair-near-me" element={<Navigate to="/iphone-repair-chicago" replace />} />
          <Route path="/sell-iphone-near-me" element={<Navigate to="/sell-iphone-chicago" replace />} />
          <Route path="/laptop-repair-near-me" element={<Navigate to="/laptop-repair-chicago" replace />} />
          {/* Local Services Hub + Individual Pages */}
          <Route path="/local-services" element={<LocalServicesHub />} />
          <Route path="/local/:slug" element={<LocalServicePage />} />
          {/* Model-Specific SEO Pages */}
          <Route path="/iphone-repair/:modelSlug" element={<IPhoneRepairSEOPage />} />
          <Route path="/samsung-repair/:modelSlug" element={<SamsungRepairSEOPage />} />
          <Route path="/ipad-repair/:modelSlug" element={<IPadRepairSEOPage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          {/* 2A Forum (community Q&A) */}
          <Route path="/forum" element={<ForumHomePage />} />
          <Route path="/forum/new" element={<ForumNewThreadPage />} />
          <Route path="/forum/search" element={<ForumSearchPage />} />
          <Route path="/forum/:category" element={<ForumCategoryPage />} />
          <Route path="/forum/:category/:slug" element={<ForumThreadPage />} />
          {/* Programmatic SEO Pages, canonical at /guides; /keywords redirects */}
          <Route path="/guides" element={<KeywordsHubPage />} />
          <Route path="/guides/:slug" element={<KeywordDetailPage />} />
          <Route path="/keywords" element={<Navigate to="/guides" replace />} />
          <Route path="/keywords/:slug" element={<KeywordsRedirect />} />
          {/* Services overview (LSA-compliant, brand-free) */}
          <Route path="/services" element={<ServicesPage />} />
          {/* What We Do (LSA-compliant, brand-free, no "repair" word) */}
          <Route path="/what-we-do" element={<WhatWeDoPage />} />
          {/* Info pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/our-team" element={<OurTeamPage />} />
          <Route path="/our-team" element={<Navigate to="/about/our-team" replace />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/terms-old" element={<TermsPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/mail-in" element={<MailInPage />} />
          <Route path="/mobile-repair-sale" element={<MobileRepairSalePage />} />
          <Route path="/price-comparison" element={<PriceComparisonPage />} />
          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/user-agreement" element={<UserAgreement />} />
          <Route path="/law-enforcement" element={<LawEnforcement />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/accessibility/feedback" element={<AccessibilityFeedback />} />
          <Route path="/dsar" element={<DSAR />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="/faq" element={<FAQFullPage />} />
          <Route path="/faqs" element={<Navigate to="/faq" replace />} />
          {/* Counteroffer Response */}
          <Route path="/counteroffer-response" element={<CounterOfferResponse />} />
          {/* Public Repair Status Tracker */}
          <Route path="/track-repair" element={<RepairStatusTracker />} />
          <Route path="/repair-status" element={<RepairStatusTracker />} />
          {/* Public Sell Status Tracker */}
          <Route path="/track-sale" element={<SellStatusTracker />} />
          <Route path="/sell-status" element={<SellStatusTracker />} />
          {/* Appointment Booking */}
          <Route path="/book" element={<BookAppointmentPage />} />
          <Route path="/book-appointment" element={<BookAppointmentPage />} />
          <Route path="/schedule" element={<BookAppointmentPage />} />
          {/* Live Repair Tracking */}
          <Route path="/track" element={<TrackRepairPage />} />
          <Route path="/track/:ticketId" element={<TrackRepairPage />} />
          <Route path="/repair-tracking" element={<TrackRepairPage />} />
          {/* Referral Program */}
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/refer" element={<ReferralPage />} />
          <Route path="/ref/:code" element={<ReferralPage />} />
          {/* Trade-In Estimator */}
          <Route path="/trade-in" element={<TradeInPage />} />
          <Route path="/tradein" element={<Navigate to="/trade-in" replace />} />
          {/* Loyalty Program */}
          <Route path="/loyalty" element={<LoyaltyPage />} />
          <Route path="/2a-club" element={<LoyaltyPage />} />
          <Route path="/rewards" element={<LoyaltyPage />} />
          {/* AI Diagnostic */}
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          <Route path="/diagnose" element={<DiagnosticPage />} />
          <Route path="/troubleshoot" element={<DiagnosticPage />} />
          {/* Warranty */}
          <Route path="/warranty" element={<WarrantyPage />} />
          <Route path="/warranty-lookup" element={<WarrantyLookupPage />} />
          <Route path="/check-warranty" element={<WarrantyLookupPage />} />
          {/* Protection Plan */}
          <Route path="/repair-protection-plan" element={<RepairProtectionPlanPage />} />
          <Route path="/protection-plan" element={<Navigate to="/repair-protection-plan" replace />} />
          {/* SMS Compliance */}
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/sms-policy" element={<SmsTermsPage />} />
          <Route path="/sms-signup" element={<SmsSignupPage />} />
          <Route path="/sms-optin" element={<SmsSignupPage />} />
          {/* E-Commerce - Shop, Refurbished, Business */}
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/parts" element={<ShopPage />} />
          <Route path="/accessories" element={<ShopPage />} />
          <Route path="/refurbished" element={<RefurbishedPage />} />
          <Route path="/refurbished/cart" element={<RefurbishedCartPage />} />
          <Route path="/refurbished/order-success" element={<RefurbishedOrderSuccess />} />
          <Route path="/refurbished/:id" element={<RefurbishedProductPage />} />
          <Route path="/certified-refurbished" element={<RefurbishedPage />} />
          <Route path="/pre-owned" element={<RefurbishedPage />} />
          <Route path="/business" element={<BusinessPage />} />
          <Route path="/corporate" element={<BusinessPage />} />
          <Route path="/b2b" element={<BusinessPage />} />
          {/* Local SEO Pages - ZIP Code Pages */}
          <Route path="/local" element={<LocalAreasPage />} />
          <Route path="/local/:zipCode" element={<LocalZipPage />} />
          <Route path="/local/service/:serviceSlug/:locationSlug" element={<LocalSEOPage />} />
          {/* Illinois Local Pages - New URL structure */}
          <Route path="/illinois/:city/:zipCode" element={<LocalZipPage />} />
          {/* New SEO Feature Pages */}
          <Route path="/google-business" element={<GoogleBusinessPage />} />
          <Route path="/social-media" element={<SocialMediaPage />} />
          <Route path="/social-media/:platform" element={<SocialPlatformPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/reviews" element={<TestimonialsPage />} />
          <Route path="/faq-answers" element={<FAQSchemaPage />} />
          <Route path="/sitemap.xml" element={<SitemapXmlRedirect />} />
          <Route path="/service-areas" element={<LocalAreasPage />} />
          {/* Apple Independent Repair Provider */}
          <Route path="/apple-repair-provider" element={<AppleRepairProviderPage />} />
          <Route path="/apple-authorized" element={<AppleRepairProviderPage />} />
          <Route path="/apple-repairs" element={<AppleRepairProviderPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          {/* Customer Portal - Unified login, repairs, and sales */}
          <Route path="/customer/login" element={<CustomerPortal />} />
          <Route path="/customer/portal" element={<CustomerPortal />} />
          <Route path="/my-account" element={<CustomerPortal />} />
          {/* Email verification landing page, from confirmation email link */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Legacy routes redirect to new portal */}
          <Route path="/register" element={<Navigate to="/customer/login" replace />} />
          <Route path="/login" element={<Navigate to="/customer/login" replace />} />
          <Route path="/profile" element={<Navigate to="/customer/portal" replace />} />
          {/* Employee Portal - Hidden URL for clock in/out */}
          <Route path="/employee-portal" element={<EmployeePortal />} />
          {/* Hidden Internal Estimator Tool - PIN-gated, not in sitemap/footer */}
          <Route path="/2aestimator" element={<Estimator2APage />} />
          {/* Admin - Changed to /admin21 for security */}
          <Route path="/admin21/login" element={<AdminLogin />} />
          <Route path="/admin21/reset" element={<AdminResetPassword />} />
          <Route path="/reset-password" element={<CustomerResetPassword />} />
          <Route path="/admin21" element={
            <ProtectedAdminRoute>
              <StoreAdminPanel />
            </ProtectedAdminRoute>
          } />
          <Route path="/admin21/resale-intelligence" element={
            <ProtectedAdminRoute>
              <ResaleIntelligence />
            </ProtectedAdminRoute>
          } />
          {/* SEO Repair Pages */}
          <Route path="/all-repairs" element={<AllRepairsPage />} />
          <Route path="/repair/:brand/:model/:service" element={<RepairServicePage />} />
          {/* SEO Sell Pages */}
          <Route path="/all-sell" element={<AllSellPage />} />
          <Route path="/sell-device/:brand/:model" element={<SellServicePage />} />
          {/* ── SEO consolidation (Jun 2026): thin city/neighborhood + service×neighborhood
              landing pages merged into the localized master hubs. 301-redirect to keep equity. ── */}
          <Route path="/sell-in/:citySlug" element={<Navigate to="/sell-phone-chicago" replace />} />
          <Route path="/repair-city/:citySlug" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/repair-in-city/:citySlug" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/repair-near/:slug" element={<RepairNearRedirect />} />
          <Route path="/sell-near/:slug" element={<Navigate to="/sell-phone-chicago" replace />} />
          <Route path="/repair-near/:slug/:service" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/sell-near/:slug/:service" element={<Navigate to="/sell-phone-chicago" replace />} />
          <Route path="/neighborhoods" element={<Navigate to="/phone-repair-chicago" replace />} />
          {/* SEO Device-Specific Sell Pages (enhanced) */}
          <Route path="/sell-my/:brand/:model" element={<SellDeviceSEOPage />} />
          {/* SEO Trade-In Comparison Pages */}
          <Route path="/sell-comparison/:comparisonSlug" element={<SellComparisonPage />} />
          {/* SEO Device Value Guide Pages */}
          <Route path="/how-much-is-my-worth/:deviceSlug" element={<DeviceValueGuidePage />} />
          {/* SEO Seasonal Sell Pages */}
          <Route path="/sell-guide/:seasonSlug" element={<SeasonalSellPage />} />
          {/* Sustainability / E-Waste Page */}
          <Route path="/sustainability" element={<SustainabilityPage />} />
          <Route path="/recycle-electronics" element={<SustainabilityPage />} />
          {/* Seller Review Pages */}
          <Route path="/seller-reviews/:categorySlug" element={<SellReviewsPage />} />
          {/* Brand Hub Pages */}
          <Route path="/sell-iphone" element={<SellBrandHubPage />} />
          <Route path="/sell-samsung" element={<SellBrandHubPage />} />
          <Route path="/sell-macbook" element={<SellBrandHubPage />} />
          <Route path="/sell-ipad" element={<SellBrandHubPage />} />
          {/* Storage-Specific Pages */}
          <Route path="/sell-storage/:storageSlug" element={<SellStoragePage />} />
          {/* Carrier-Specific Pages */}
          <Route path="/sell-carrier/:carrierSlug" element={<SellCarrierPage />} />
          {/* Sell vs Keep Calculator */}
          <Route path="/sell-or-keep-calculator" element={<SellOrKeepCalculator />} />
          {/* State-Specific Sell Pages */}
          <Route path="/sell-phone-in/:stateSlug" element={<SellStatePage />} />
          {/* Year-Based Sell Pages */}
          <Route path="/sell-year/:yearSlug" element={<SellYearPage />} />
          {/* Bundle Sell Page */}
          <Route path="/sell-multiple-devices" element={<BundleSellPage />} />
          {/* Repair SEO - Brand Hub Pages */}
          <Route path="/repair-iphone" element={<RepairBrandHubPage />} />
          <Route path="/repair-samsung" element={<RepairBrandHubPage />} />
          <Route path="/repair-macbook" element={<RepairBrandHubPage />} />
          <Route path="/repair-ipad" element={<RepairBrandHubPage />} />
          {/* Repair SEO - Comparison Pages */}
          <Route path="/repair-comparison/:comparisonSlug" element={<RepairComparisonPage />} />
          {/* Repair SEO - Cost Guide Pages */}
          <Route path="/repair-cost/:guideSlug" element={<RepairCostGuidePage />} />
          {/* Repair SEO - State Pages */}
          <Route path="/repair-in/:stateSlug" element={<RepairStatePage />} />
          {/* Repair SEO - Review Pages */}
          <Route path="/repair-reviews/:categorySlug" element={<RepairReviewsPage />} />
          {/* Repair or Replace Calculator */}
          <Route path="/repair-or-replace-calculator" element={<RepairOrReplaceCalculator />} />
          {/* Symptom Checker */}
          <Route path="/symptom-checker" element={<SymptomCheckerPage />} />
          {/* Free Tools Suite (SEO backlink magnets) */}
          <Route path="/free-repair-resources" element={<FreeRepairResourcesPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/tools" element={<ToolsHubPage />} />
          <Route path="/tools/imei-checker" element={<IMEICheckerPage />} />
          <Route path="/tools/repair-cost-calculator" element={<RepairCostCalculatorPage />} />
          <Route path="/tools/trade-in-estimator" element={<TradeInEstimatorPage />} />
          <Route path="/tools/water-damage-quiz" element={<WaterDamageQuizPage />} />
          {/* SEO consolidation (Jun 2026): device × symptom problem pages → brand hubs */}
          <Route path="/fix/:device/:symptom" element={<FixSymptomRedirect />} />
          {/* SEO Growth 2, Pillar Authority Hubs */}
          <Route path="/iphone-hub" element={<PillariPhoneHub />} />
          <Route path="/samsung-hub" element={<PillarSamsungHub />} />
          <Route path="/macbook-hub" element={<PillarMacBookHub />} />
          {/* SEO Growth 2, Authors / Q&A / Comparison / Gallery / Press */}
          <Route path="/team" element={<AuthorsTeamPage />} />
          <Route path="/team/:slug" element={<AuthorsTeamPage />} />
          <Route path="/qa" element={<QAIndexPage />} />
          <Route path="/qa/:slug" element={<QAPage />} />
          <Route path="/compare/:slug" element={<ComparisonPage />} />
          <Route path="/ubreakifix-alternative" element={<UbreakifixAlternativePage />} />
          <Route path="/alternative-to-ubreakifix" element={<UbreakifixAlternativePage />} />
          <Route path="/2aelectronics-review-ai-seo" element={<ReviewsAiSeoPage />} />
          <Route path="/2a-reviews-ai" element={<ReviewsAiSeoPage />} />
          <Route path="/repair-gallery" element={<BeforeAfterGallery />} />
          <Route path="/press-kit" element={<PressKitPage />} />
          <Route path="/whats-wrong-with-my-phone" element={<SymptomCheckerPage />} />
          <Route path="/device-diagnosis" element={<SymptomCheckerPage />} />
          {/* Repair SEO Content Pages */}
          <Route path="/repair-how-it-works" element={<HowItWorksRepairPage />} />
          <Route path="/repair-checklist" element={<RepairChecklistPage />} />
          <Route path="/repair-faq" element={<FAQRepairPage />} />
          <Route path="/repair-environmental-impact" element={<EnvironmentalImpactRepairPage />} />
          <Route path="/repair-data-security" element={<DataSecurityRepairPage />} />
          <Route path="/why-choose-us-repair" element={<WhyUsRepairPage />} />
          <Route path="/repair-vs-buy-new" element={<RepairVsBuyNewPage />} />
          {/* Long-Form SEO Content Hub Pages */}
          <Route path="/guide/iphone-repair-guide-2026" element={<CompleteRepairGuide />} />
          <Route path="/guide/how-to-sell-phone-2026" element={<CompleteSellGuide />} />

          {/* Programmatic SEO Landing Pages - Core Service */}
          <Route path="/phone-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/iphone-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-my-phone-chicago" element={<Navigate to="/sell-phone-chicago" replace />} />
          <Route path="/iphone-screen-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/samsung-phone-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/ipad-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/laptop-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/macbook-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/computer-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/cracked-screen-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/game-console-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/charging-port-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/phone-battery-replacement-chicago" element={<SEOLandingPage />} />
          <Route path="/microsoldering-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/electronics-repair-chicago" element={<SEOLandingPage />} />
          <Route path="/cash-for-phones-chicago" element={<SEOLandingPage />} />
          <Route path="/we-buy-phones-chicago" element={<Navigate to="/cash-for-phones-chicago" replace />} />

          {/* SEO consolidation (Jun 2026): individual device-problem pages → brand hubs / repair master */}
          <Route path="/phone-not-charging-repair" element={<Navigate to="/charging-port-repair-chicago" replace />} />
          <Route path="/phone-not-turning-on-repair" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/iphone-stuck-on-apple-logo" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/phone-water-damage-repair" element={<Navigate to="/microsoldering-repair-chicago" replace />} />
          <Route path="/phone-overheating-repair" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/phone-battery-draining-fast" element={<Navigate to="/phone-repair-chicago" replace />} />

          {/* SEO consolidation (Jun 2026): repair-type pages → brand hubs / repair master */}
          <Route path="/iphone-screen-repair" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/iphone-battery-replacement" element={<Navigate to="/phone-battery-replacement-chicago" replace />} />
          <Route path="/charging-port-repair" element={<Navigate to="/charging-port-repair-chicago" replace />} />
          <Route path="/speaker-repair" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/camera-repair" element={<Navigate to="/phone-repair-chicago" replace />} />
          <Route path="/motherboard-repair" element={<Navigate to="/phone-repair-chicago" replace />} />

          {/* SEO consolidation (Jun 2026): repair-cost pages → brand hubs / repair master */}
          <Route path="/iphone-screen-repair-cost" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/iphone-battery-replacement-cost" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/charging-port-repair-cost" element={<Navigate to="/charging-port-repair-chicago" replace />} />
          <Route path="/samsung-screen-repair-cost" element={<Navigate to="/samsung-hub" replace />} />

          {/* Programmatic SEO - Sell / Buyback Pages */}
          <Route path="/buyback" element={<BuybackPage />} />
          <Route path="/sell-phone-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-iphone-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-samsung-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-macbook-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-ipad-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-laptop-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-broken-phone-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-sealed-apple-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-sealed-iphone-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-sealed-macbook-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-sealed-airpods-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-apple-gift-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-bnib-apple-chicago" element={<SEOLandingPage />} />
          <Route path="/trade-in-phone-chicago" element={<SEOLandingPage />} />
          <Route path="/sell-broken-phone" element={<SEOLandingPage />} />
          <Route path="/sell-phone-for-cash" element={<Navigate to="/sell-iphone" replace />} />
          <Route path="/sell-broken-iphone" element={<SEOLandingPage />} />
          <Route path="/sell-cracked-iphone" element={<Navigate to="/sell-broken-iphone" replace />} />
          <Route path="/sell-water-damaged-iphone" element={<Navigate to="/sell-broken-iphone" replace />} />
          <Route path="/how-much-is-my-phone-worth" element={<SEOLandingPage />} />
          <Route path="/iphone-trade-in-value" element={<SEOLandingPage />} />
          <Route path="/samsung-trade-in-value" element={<SEOLandingPage />} />

          {/* Chicago neighborhood repair pages, re-enabled Jun 2026 (each has unique localized
              content: directions, transit, neighborhood-specific FAQs). All link back to the
              /phone-repair-chicago master hub. */}
          <Route path="/phone-repair-irving-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-logan-square" element={<SEOLandingPage />} />
          <Route path="/phone-repair-avondale" element={<SEOLandingPage />} />
          <Route path="/phone-repair-portage-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-norridge" element={<SEOLandingPage />} />
          <Route path="/phone-repair-elmwood-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-lincoln-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-skokie" element={<SEOLandingPage />} />
          <Route path="/phone-repair-evanston" element={<SEOLandingPage />} />
          <Route path="/phone-repair-albany-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-jefferson-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-lincoln-square" element={<SEOLandingPage />} />
          <Route path="/phone-repair-lakeview" element={<SEOLandingPage />} />
          <Route path="/phone-repair-wicker-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-old-irving-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-north-center" element={<SEOLandingPage />} />
          <Route path="/phone-repair-roscoe-village" element={<SEOLandingPage />} />
          <Route path="/phone-repair-humboldt-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-belmont-cragin" element={<SEOLandingPage />} />
          <Route path="/phone-repair-harwood-heights" element={<SEOLandingPage />} />
          <Route path="/phone-repair-schiller-park" element={<SEOLandingPage />} />
          <Route path="/phone-repair-river-grove" element={<SEOLandingPage />} />
          <Route path="/phone-repair-niles" element={<SEOLandingPage />} />

          {/* Comparison Pages */}
          <Route path="/2a-electronics-vs-apple-store" element={<SEOLandingPage />} />
          <Route path="/2a-electronics-vs-ubreakifix" element={<SEOLandingPage />} />
          <Route path="/2a-electronics-vs-best-buy" element={<SEOLandingPage />} />
          <Route path="/independent-vs-franchise-repair" element={<SEOLandingPage />} />
          <Route path="/phone-repair-diy-vs-professional" element={<SEOLandingPage />} />

          {/* Spanish & Polish landing pages, 301-equivalent redirects to English masters.
              Migration review (Jun 2026): old iPhone-repair URLs → /iphone-hub, old Samsung URLs
              → /samsung-hub, and broken redirect targets (mail-in/screen-replacement/galaxy-repair/
              repair-warranty pages that no longer exist) repointed to live pages, no more 404 chains. */}
          <Route path="/reparacion-de-telefonos-chicago" element={<Navigate to="/repair-services-chicago" replace />} />
          <Route path="/reparacion-de-iphone-chicago" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/vender-telefono-chicago" element={<Navigate to="/sell-devices-chicago" replace />} />
          <Route path="/reparacion-de-pantalla-iphone" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/compra-de-telefonos-chicago" element={<Navigate to="/sell-devices-chicago" replace />} />
          <Route path="/reparacion-de-samsung-chicago" element={<Navigate to="/samsung-hub" replace />} />
          <Route path="/reparacion-de-macbook-chicago" element={<Navigate to="/macbook-repair-chicago" replace />} />
          <Route path="/reparacion-de-ipad-chicago" element={<Navigate to="/ipad-repair-chicago" replace />} />
          <Route path="/reparacion-de-consolas-chicago" element={<Navigate to="/repairs" replace />} />
          <Route path="/vender-mi-macbook" element={<Navigate to="/sell-macbook" replace />} />
          <Route path="/vender-mi-ipad" element={<Navigate to="/sell-ipad" replace />} />
          <Route path="/vender-mi-samsung" element={<Navigate to="/sell-samsung" replace />} />
          <Route path="/reparacion-cerca-de-mi" element={<Navigate to="/repairs" replace />} />
          <Route path="/garantia-de-reparacion" element={<Navigate to="/warranty" replace />} />
          <Route path="/envio-por-correo-reparacion" element={<Navigate to="/repairs" replace />} />
          <Route path="/naprawa-telefonow-chicago" element={<Navigate to="/repair-services-chicago" replace />} />
          <Route path="/naprawa-iphone-chicago" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/naprawa-samsung-chicago" element={<Navigate to="/samsung-hub" replace />} />
          <Route path="/naprawa-laptopow-chicago" element={<Navigate to="/macbook-repair-chicago" replace />} />
          <Route path="/sprzedaj-telefon-chicago" element={<Navigate to="/sell-devices-chicago" replace />} />
          <Route path="/sprzedaj-iphone" element={<Navigate to="/sell-iphone" replace />} />
          <Route path="/naprawa-ekranu-iphone" element={<Navigate to="/iphone-hub" replace />} />
          <Route path="/naprawa-blisko-mnie" element={<Navigate to="/repairs" replace />} />
          <Route path="/serwis-wysylkowy" element={<Navigate to="/repairs" replace />} />
          <Route path="/gwarancja-naprawy" element={<Navigate to="/warranty" replace />} />
          <Route path="/uslugi-po-polsku" element={<Navigate to="/services" replace />} />
          <Route path="/servicios-en-espanol" element={<Navigate to="/services" replace />} />

          {/* "Is It Worth Repairing?" Guide Pages */}
          <Route path="/is-it-worth-repairing-iphone" element={<SEOLandingPage />} />
          <Route path="/is-it-worth-repairing-macbook" element={<SEOLandingPage />} />
          <Route path="/is-it-worth-repairing-ipad" element={<SEOLandingPage />} />
          <Route path="/is-it-worth-repairing-samsung" element={<SEOLandingPage />} />
          <Route path="/is-it-worth-repairing-ps5" element={<SEOLandingPage />} />
          <Route path="/is-it-worth-repairing-nintendo-switch" element={<SEOLandingPage />} />

          {/* SEO Category Hub Pages */}
          <Route path="/repair-services-chicago" element={<Navigate to="/repairs" replace />} />
          <Route path="/sell-devices-chicago" element={<SEOCategoryHub />} />
          <Route path="/repair-cost-guides" element={<SEOCategoryHub />} />
          <Route path="/chicago-neighborhoods" element={<SEOCategoryHub />} />
          <Route path="/compare-repair-shops" element={<SEOCategoryHub />} />
          <Route path="/worth-repairing-guides" element={<SEOCategoryHub />} />

          {/* Case Studies */}
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />

          {/* Repair Quote Lead Form */}
          <Route path="/repair-quote" element={<RepairQuotePage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/screen-replacement" element={<ScreenReplacementPage />} />

          {/* AI Answers Hub, Answer Engine Optimization (AEO) for ChatGPT/Perplexity/Claude/Gemini */}
          <Route path="/answers" element={<AnswersHub />} />
          <Route path="/answers/:slug" element={<AnswerDetailPage />} />

          {/* Curated nearby-city landing pages, 8 high-volume suburbs with unique local content */}
          <Route path="/nearby/:slug" element={<NearbyCityLandingPage />} />

          {/* Admin: AI Rank Monitor (AEO scoreboard) */}
          <Route path="/admin21/ai-rank" element={<AIRankMonitorPage />} />

          {/* Admin: SEO Growth, Citation Blitz + AI Content Engine */}
          <Route path="/admin21/seo-growth" element={<SeoGrowthAdminPage />} />
          <Route path="/admin21/search-console" element={<ProtectedAdminRoute><SearchConsoleAdminPage /></ProtectedAdminRoute>} />

          {/* Public: Press kit / Media room */}
          <Route path="/press" element={<PressPage />} />
          {/* Public: OEM partnership applications */}
          <Route path="/partnerships" element={<PartnershipsPage />} />

          {/* Web Design Microsite */}
          <Route path="/webdesign" element={<WebDesignLayout />}>
            <Route index element={<WebDesignHome />} />
            <Route path="services" element={<WebDesignServices />} />
            <Route path="how-it-works" element={<WebDesignHowItWorks />} />
            <Route path="about" element={<WebDesignAbout />} />
            <Route path="contact" element={<WebDesignContact />} />
          </Route>

          {/* 404 - Catch all unmatched routes */}
          {/* Redirect old sell-brand URLs to proper sell paths */}
          <Route path="/sell-brand/:rest" element={<Navigate to="/sell" replace />} />
          <Route path="/sell-brand/:cat/:model" element={<Navigate to="/sell" replace />} />
          {/* Redirect old sell-devices/states URLs (no longer valid) */}
          <Route path="/sell-devices/*" element={<Navigate to="/sell" replace />} />
          <Route path="*" element={<SmartLegacyNotFound />} />
        </Routes>
      </Suspense>
      </main>
      <ConditionalFooter />
      {!isWebDesignRoute && <Suspense fallback={null}><WhatsAppButton /></Suspense>}
      {!isWebDesignRoute && <Suspense fallback={null}><AccessibilityWidget /></Suspense>}
      <Toaster />
    </>
  );
}

export default App;